import { consentManager } from '../services/ConsentManager';

interface ScriptConfig {
  src: string;
  category: 'required' | 'analytics' | 'marketing';
  async?: boolean;
  defer?: boolean;
  attributes?: Record<string, string>;
  onLoad?: () => void;
  onError?: () => void;
}

interface PendingScript extends ScriptConfig {
  element?: HTMLScriptElement;
  loaded: boolean;
  blocked: boolean;
}

class ScriptManager {
  private pendingScripts: Map<string, PendingScript> = new Map();
  private loadedScripts: Set<string> = new Set();
  private originalCreateElement: typeof document.createElement;

  constructor() {
    this.originalCreateElement = document.createElement;
    this.interceptScriptCreation();
    this.setupConsentWatcher();
  }

  /**
   * Registriert ein Script für conditional loading
   */
  registerScript(config: ScriptConfig): void {
    const id = this.generateScriptId(config.src);
    
    this.pendingScripts.set(id, {
      ...config,
      loaded: false,
      blocked: !this.hasPermissionForCategory(config.category)
    });

    // Sofort laden wenn Berechtigung vorhanden
    if (this.hasPermissionForCategory(config.category)) {
      this.loadScript(id);
    }
  }

  /**
   * Lädt alle Scripts einer bestimmten Kategorie
   */
  loadScriptsByCategory(category: 'required' | 'analytics' | 'marketing'): void {
    this.pendingScripts.forEach((script, id) => {
      if (script.category === category && script.blocked && !script.loaded) {
        script.blocked = false;
        this.loadScript(id);
      }
    });
  }

  /**
   * Blockiert alle Scripts einer bestimmten Kategorie
   */
  blockScriptsByCategory(category: 'required' | 'analytics' | 'marketing'): void {
    this.pendingScripts.forEach((script, id) => {
      if (script.category === category) {
        script.blocked = true;
        if (script.element && script.element.parentNode) {
          // Script-Element entfernen wenn geladen
          script.element.parentNode.removeChild(script.element);
          script.loaded = false;
        }
      }
    });
  }

  /**
   * Lädt ein spezifisches Script
   */
  private loadScript(id: string): void {
    const script = this.pendingScripts.get(id);
    if (!script || script.loaded || script.blocked) {
      return;
    }

    const scriptElement = document.createElement('script');
    scriptElement.src = script.src;
    
    if (script.async !== undefined) {
      scriptElement.async = script.async;
    }
    
    if (script.defer !== undefined) {
      scriptElement.defer = script.defer;
    }

    // Zusätzliche Attribute setzen
    if (script.attributes) {
      Object.entries(script.attributes).forEach(([key, value]) => {
        scriptElement.setAttribute(key, value);
      });
    }

    // Event-Handler
    scriptElement.onload = () => {
      script.loaded = true;
      this.loadedScripts.add(script.src);
      script.onLoad?.();
      console.log(`✅ Script loaded: ${script.src} (${script.category})`);
    };

    scriptElement.onerror = () => {
      console.error(`❌ Script failed to load: ${script.src} (${script.category})`);
      script.onError?.();
    };

    // Script hinzufügen
    script.element = scriptElement;
    document.head.appendChild(scriptElement);
  }

  /**
   * Überwacht Script-Erstellung im DOM
   */
  private interceptScriptCreation(): void {
    document.createElement = ((tagName: string) => {
      const element = this.originalCreateElement.call(document, tagName);
      
      if (tagName.toLowerCase() === 'script') {
        const scriptElement = element as HTMLScriptElement;
        
        // Override setAttribute für src-Attribute
        const originalSetAttribute = scriptElement.setAttribute;
        scriptElement.setAttribute = (name: string, value: string) => {
          if (name === 'src' && this.shouldBlockScript(value)) {
            console.log(`🚫 Script blocked: ${value}`);
            return; // Blockiert das Setzen der src
          }
          return originalSetAttribute.call(scriptElement, name, value);
        };

        // Override src property
        let srcValue = '';
        Object.defineProperty(scriptElement, 'src', {
          get() { return srcValue; },
          set(value: string) {
            if (this.shouldBlockScript(value)) {
              console.log(`🚫 Script blocked via property: ${value}`);
              return;
            }
            srcValue = value;
            originalSetAttribute.call(scriptElement, 'src', value);
          }
        });
      }
      
      return element;
    }) as typeof document.createElement;
  }

  /**
   * Prüft ob ein Script blockiert werden soll
   */
  private shouldBlockScript(src: string): boolean {
    // Analytics Scripts (Google Analytics, etc.)
    const analyticsPatterns = [
      /google-analytics/i,
      /googletagmanager/i,
      /gtag/i,
      /facebook\.net.*\/tr/i,
      /doubleclick/i,
      /analytics/i
    ];

    // Marketing Scripts (Tracking, Ads, etc.)
    const marketingPatterns = [
      /facebook\.com.*\/tr/i,
      /connect\.facebook/i,
      /ads-twitter/i,
      /linkedin\.com.*\/analytics/i,
      /hotjar/i,
      /crazyegg/i,
      /mixpanel/i,
      /segment/i
    ];

    // Prüfe Analytics
    if (analyticsPatterns.some(pattern => pattern.test(src))) {
      return !this.hasPermissionForCategory('analytics');
    }

    // Prüfe Marketing
    if (marketingPatterns.some(pattern => pattern.test(src))) {
      return !this.hasPermissionForCategory('marketing');
    }

    // Standardmäßig erlauben (required scripts)
    return false;
  }

  /**
   * Überwacht Consent-Änderungen
   */
  private setupConsentWatcher(): void {
    // Überwache localStorage Änderungen
    window.addEventListener('storage', (event) => {
      if (event.key === 'smart_storage_gdpr_consent') {
        this.updateScriptsBasedOnConsent();
      }
    });

    // Initial update
    this.updateScriptsBasedOnConsent();
  }

  /**
   * Aktualisiert Script-Loading basierend auf aktueller Einwilligung
   */
  private updateScriptsBasedOnConsent(): void {
    // Analytics Scripts
    if (this.hasPermissionForCategory('analytics')) {
      this.loadScriptsByCategory('analytics');
    } else {
      this.blockScriptsByCategory('analytics');
    }

    // Marketing Scripts
    if (this.hasPermissionForCategory('marketing')) {
      this.loadScriptsByCategory('marketing');
    } else {
      this.blockScriptsByCategory('marketing');
    }

    // Required Scripts immer laden
    this.loadScriptsByCategory('required');
  }

  /**
   * Prüft Berechtigung für Kategorie
   */
  private hasPermissionForCategory(category: 'required' | 'analytics' | 'marketing'): boolean {
    if (category === 'required') {
      return true; // Required scripts immer erlaubt
    }
    
    return consentManager.hasPermission(category);
  }

  /**
   * Generiert eindeutige Script-ID
   */
  private generateScriptId(src: string): string {
    return btoa(src).replace(/[+/=]/g, '');
  }

  /**
   * Debugging-Informationen
   */
  getDebugInfo(): object {
    // Convert Map to object manually for compatibility
    const pendingScriptsObj: Record<string, any> = {};
    this.pendingScripts.forEach((value, key) => {
      pendingScriptsObj[key] = value;
    });

    return {
      pendingScripts: pendingScriptsObj,
      loadedScripts: Array.from(this.loadedScripts),
      consent: {
        required: this.hasPermissionForCategory('required'),
        analytics: this.hasPermissionForCategory('analytics'),
        marketing: this.hasPermissionForCategory('marketing')
      }
    };
  }

  /**
   * Cleanup (für Tests oder Entwicklung)
   */
  destroy(): void {
    document.createElement = this.originalCreateElement;
    this.pendingScripts.clear();
    this.loadedScripts.clear();
  }
}

// Singleton-Export
export const scriptManager = new ScriptManager();
export default ScriptManager;

// Utility-Funktionen für einfache Verwendung
export const registerAnalyticsScript = (src: string, options: Partial<ScriptConfig> = {}) => {
  scriptManager.registerScript({
    ...options,
    src,
    category: 'analytics'
  });
};

export const registerMarketingScript = (src: string, options: Partial<ScriptConfig> = {}) => {
  scriptManager.registerScript({
    ...options,
    src,
    category: 'marketing'
  });
};

export const registerRequiredScript = (src: string, options: Partial<ScriptConfig> = {}) => {
  scriptManager.registerScript({
    ...options,
    src,
    category: 'required'
  });
};